import {
  Injectable,
  InjectionToken,
  Injector,
  StaticProvider,
  Type,
} from '@angular/core';
import { Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';

import { ModalRef } from './modal-ref';
import { ModalConfig } from './modal.config';

export const ModalData = new InjectionToken<any>('PSUiModalData');

@Injectable({
  providedIn: 'root',
})
export class ModalService {
  private _overlayRef?: OverlayRef;

  constructor(private _overlay: Overlay, private _injector: Injector) {}

  open<T, U = any, R = any>(
    component: Type<T>,
    config: ModalConfig<U> = {}
  ): ModalRef<T, R> {
    config = { ...new ModalConfig(), ...config };
    const overlayConfig = this._getOverlayConfig(config);
    this._overlayRef = this._overlay.create(overlayConfig);

    const modalRef = new ModalRef<T, R>(this._overlayRef, config);

    const injector = this._createInjector(modalRef, config);
    const portal = new ComponentPortal(component, null, injector);
    const componentRef = this._overlayRef.attach(portal);

    modalRef.componentInstance = componentRef.instance;

    return modalRef;
  }

  private _getOverlayConfig(config: ModalConfig): OverlayConfig {
    const strategy = this._overlay.position().global();
    if (config.position && (config.position.right || config.position.left)) {
      config.position.left
        ? strategy.left(config.position.left)
        : strategy.right(config.position.right);
    } else {
      strategy.centerHorizontally();
    }

    if (config.position && (config.position.top || config.position.bottom)) {
      config.position.bottom
        ? strategy.bottom(config.position.bottom)
        : strategy.top(config.position.top);
    } else {
      strategy.centerVertically();
    }

    return new OverlayConfig({
      hasBackdrop: true,
      positionStrategy: strategy,
      minWidth: config.minWidth,
      minHeight: config.minHeight,
      maxWidth: config.maxWidth,
      maxHeight: config.maxHeight,
      width: config.width,
      height: config.height,
    });
  }

  /**
   * Create a new injector for the modal to use that provides the modal ref and modal data
   */
  private _createInjector<T>(
    modalRef: ModalRef<T>,
    config: ModalConfig
  ): Injector {
    const providers: StaticProvider[] = [
      { provide: ModalRef, useValue: modalRef },
      { provide: ModalData, useValue: config.data },
    ];

    return Injector.create({ parent: this._injector, providers });
  }
}
